import {
  getObjectProperties,
  ObjectType,
  TILE_SIZE,
  toObjectType,
  type WorldObjectSnapshot,
} from '@dreamgame/shared';
import { computed, effect, signal } from '@preact/signals';
import { getAllObjects } from './chunks';
import { type PawnData, pawns, pawnsVersion, playerId } from './entities';
import { getNpcAtPosition, type NpcData, npcs } from './npcs';
import { deselectTerritory, getTerritoryByFlagPosition, selectTerritory } from './territories';

// ============================================================================
// Types
// ============================================================================

export interface SelectionBox {
  startX: number;
  startY: number;
  endX: number;
  endY: number;
}

interface DragState {
  active: boolean;
  isDragging: boolean;
  suppressDrag: boolean;
  startX: number;
  startY: number;
  screenStartX: number;
  screenStartY: number;
}

export const SelectionType = {
  NONE: 'none',
  PAWN: 'pawn',
  ANIMAL: 'animal',
  WORLD_OBJECT: 'worldObject',
} as const;

export type SelectionTypeValue = (typeof SelectionType)[keyof typeof SelectionType];

// ============================================================================
// Constants
// ============================================================================

const TERRITORY_FLAG_TYPES: ReadonlySet<number> = new Set([
  ObjectType.CLAIM_FLAG_HOME,
  ObjectType.CLAIM_FLAG_OUTPOST,
]);

const DRAG_THRESHOLD = 5;

// ============================================================================
// State
// ============================================================================

export const selectedIds = signal<Set<number>>(new Set());
export const selectionBox = signal<SelectionBox | null>(null);
export const selectedNpcId = signal<number | null>(null);
export const selectedWorldObjectId = signal<number | null>(null);
export const selectionType = signal<SelectionTypeValue>(SelectionType.NONE);

// Version counter for selection changes - incremented when selection content changes
export const selectionVersion = signal(0);

const dragState = signal<DragState>({
  active: false,
  isDragging: false,
  suppressDrag: false,
  startX: 0,
  startY: 0,
  screenStartX: 0,
  screenStartY: 0,
});

// Colonist portrait positions for unified drag selection (screen coordinates)
// Plain Map - no signal needed since it's only read during drag completion
export const colonistPortraitRects = new Map<number, DOMRect>();

// ============================================================================
// Effects
// ============================================================================

// Sync pawn selection to server so AI can suppress wandering for selected pawns
// Uses dynamic import to avoid circular dependency with NetworkClient
effect(() => {
  const ids = selectedIds.value;
  import('../network/NetworkClient').then(({ sendPawnSelection }) => {
    sendPawnSelection({ selectedPawnIds: Array.from(ids) });
  });
});

export const registerColonistPortrait = ({
  networkId,
  rect,
}: {
  networkId: number;
  rect: DOMRect;
}): void => {
  colonistPortraitRects.set(networkId, rect);
};

export const unregisterColonistPortrait = ({ networkId }: { networkId: number }): void => {
  colonistPortraitRects.delete(networkId);
};

export const clearColonistPortraitRects = (): void => {
  colonistPortraitRects.clear();
};

// ============================================================================
// Derived State
// ============================================================================

// PERFORMANCE: Double-buffer selected pawns to avoid allocations and still
// provide a new array reference when dependencies change. This keeps
// subscribers reactive while avoiding per-frame array creation.
let _selectedPawnsReadCache: PawnData[] = [];
let _selectedPawnsWriteCache: PawnData[] = [];
let _selectedPawnsCachePawnsVersion = -1;
let _selectedPawnsCacheSelectionVersion = -1;

export const selectedPawns = computed((): PawnData[] => {
  const pawnVer = pawnsVersion.value;
  const selVer = selectionVersion.value;

  // Return cached result if dependencies haven't changed
  if (
    pawnVer === _selectedPawnsCachePawnsVersion &&
    selVer === _selectedPawnsCacheSelectionVersion
  ) {
    return _selectedPawnsReadCache;
  }

  // Rebuild cache - only when selection or pawns actually change.
  _selectedPawnsCachePawnsVersion = pawnVer;
  _selectedPawnsCacheSelectionVersion = selVer;

  _selectedPawnsWriteCache.length = 0;

  for (const id of selectedIds.value) {
    const pawn = pawns.value.get(id);
    if (pawn) {
      _selectedPawnsWriteCache.push(pawn);
    }
  }

  const nextReadCache = _selectedPawnsWriteCache;
  _selectedPawnsWriteCache = _selectedPawnsReadCache;
  _selectedPawnsReadCache = nextReadCache;

  return _selectedPawnsReadCache;
});

export const selectedNpc = computed((): NpcData | null => {
  const animalId = selectedNpcId.value;
  if (animalId === null) {
    return null;
  }
  return npcs.value.get(animalId) ?? null;
});

export const selectedWorldObject = computed((): WorldObjectSnapshot | null => {
  const objectId = selectedWorldObjectId.value;
  if (objectId === null) {
    return null;
  }
  const allObjects = getAllObjects();
  return allObjects.find((obj) => obj.networkId === objectId) ?? null;
});

export const hasSelection = computed(
  () =>
    selectedIds.value.size > 0 ||
    selectedNpcId.value !== null ||
    selectedWorldObjectId.value !== null
);

export const hasPawnSelection = computed(() => selectedIds.value.size > 0);

export const hasNpcSelection = computed(() => selectedNpcId.value !== null);

export const hasWorldObjectSelection = computed(() => selectedWorldObjectId.value !== null);

export const isDragSelecting = computed(() => dragState.value.isDragging);

// ============================================================================
// Internal Helpers
// ============================================================================

const clearOtherSelections = ({ keepPawns = false }: { keepPawns?: boolean } = {}): void => {
  if (!keepPawns) {
    selectedIds.value = new Set();
    selectionVersion.value = selectionVersion.value + 1;
  }
  selectedNpcId.value = null;
  selectedWorldObjectId.value = null;
  deselectTerritory();
};

const updateSelectionType = (): void => {
  if (selectedIds.value.size > 0) {
    selectionType.value = SelectionType.PAWN;
  } else if (selectedNpcId.value !== null) {
    selectionType.value = SelectionType.ANIMAL;
  } else if (selectedWorldObjectId.value !== null) {
    selectionType.value = SelectionType.WORLD_OBJECT;
  } else {
    selectionType.value = SelectionType.NONE;
  }
};

const getWorldObjectAtPosition = ({
  tileX,
  tileY,
}: {
  tileX: number;
  tileY: number;
}): WorldObjectSnapshot | null => {
  const allObjects = getAllObjects();

  for (const obj of allObjects) {
    const objectType = toObjectType(obj.objectType);
    const props = getObjectProperties({ objectType });
    const objMinX = obj.tileX;
    const objMaxX = obj.tileX + props.width - 1;
    const objMinY = obj.tileY;
    const objMaxY = obj.tileY + props.height - 1;

    if (tileX >= objMinX && tileX <= objMaxX && tileY >= objMinY && tileY <= objMaxY) {
      return obj;
    }
  }

  return null;
};

interface ScreenBox {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

const rectsIntersect = ({
  r1,
  r2,
}: {
  r1: ScreenBox;
  r2: { left: number; top: number; right: number; bottom: number };
}): boolean => {
  return !(r2.left > r1.right || r2.right < r1.left || r2.top > r1.bottom || r2.bottom < r1.top);
};

// Find pawns in world space box
const findPawnsInWorldBox = ({
  minX,
  maxX,
  minY,
  maxY,
  currentPlayerId,
}: {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
  currentPlayerId: number;
}): Set<number> => {
  const selection = new Set<number>();
  for (const pawn of pawns.value.values()) {
    if (pawn.ownerId !== currentPlayerId) {
      continue;
    }
    const pawnCenterX = pawn.x * TILE_SIZE + TILE_SIZE / 2;
    const pawnCenterY = pawn.y * TILE_SIZE + TILE_SIZE / 2;
    if (pawnCenterX >= minX && pawnCenterX <= maxX && pawnCenterY >= minY && pawnCenterY <= maxY) {
      selection.add(pawn.networkId);
    }
  }
  return selection;
};

// Find pawns in screen space portraits
const findPawnsInScreenBox = ({
  screenBox,
  currentPlayerId,
}: {
  screenBox: ScreenBox;
  currentPlayerId: number;
}): Set<number> => {
  const selection = new Set<number>();
  for (const [pawnId, rect] of colonistPortraitRects.entries()) {
    const pawn = pawns.value.get(pawnId);
    if (!pawn || pawn.ownerId !== currentPlayerId) {
      continue;
    }
    if (rectsIntersect({ r1: screenBox, r2: rect })) {
      selection.add(pawnId);
    }
  }
  return selection;
};

const selectPawnsInBox = ({
  box,
  screenBox,
}: {
  box: SelectionBox;
  screenBox: ScreenBox | null;
}): void => {
  const minX = Math.min(box.startX, box.endX);
  const maxX = Math.max(box.startX, box.endX);
  const minY = Math.min(box.startY, box.endY);
  const maxY = Math.max(box.startY, box.endY);
  const currentPlayerId = playerId.value;

  const worldSelection = findPawnsInWorldBox({ minX, maxX, minY, maxY, currentPlayerId });
  const screenSelection = screenBox
    ? findPawnsInScreenBox({ screenBox, currentPlayerId })
    : new Set<number>();

  const newSelection = new Set([...worldSelection, ...screenSelection]);

  selectedIds.value = newSelection;
  selectionVersion.value = selectionVersion.value + 1;
  updateSelectionType();
};

// ============================================================================
// Public Actions
// ============================================================================

export const selectPawn = ({
  networkId,
  additive = false,
}: {
  networkId: number;
  additive?: boolean;
}): void => {
  const pawn = pawns.value.get(networkId);
  if (!pawn || pawn.ownerId !== playerId.value) {
    return;
  }

  clearOtherSelections({ keepPawns: additive });

  if (additive) {
    const next = new Set(selectedIds.value);
    if (next.has(networkId)) {
      next.delete(networkId);
    } else {
      next.add(networkId);
    }
    selectedIds.value = next;
  } else {
    selectedIds.value = new Set([networkId]);
  }
  selectionVersion.value = selectionVersion.value + 1;

  updateSelectionType();
};

export const deselectPawn = ({ networkId }: { networkId: number }): void => {
  const next = new Set(selectedIds.value);
  next.delete(networkId);
  selectedIds.value = next;
  selectionVersion.value = selectionVersion.value + 1;
  updateSelectionType();
};

export const selectAllPawns = (): void => {
  const ownedPawnIds = new Set<number>();
  const currentPlayerId = playerId.value;

  for (const pawn of pawns.value.values()) {
    if (pawn.ownerId === currentPlayerId) {
      ownedPawnIds.add(pawn.networkId);
    }
  }

  if (ownedPawnIds.size > 0) {
    clearOtherSelections();
    selectedIds.value = ownedPawnIds;
    selectionVersion.value = selectionVersion.value + 1;
    updateSelectionType();
  }
};

export const selectPawns = ({ networkIds }: { networkIds: number[] }): void => {
  const currentPlayerId = playerId.value;
  const validIds = new Set<number>();

  for (const networkId of networkIds) {
    const pawn = pawns.value.get(networkId);
    if (pawn && pawn.ownerId === currentPlayerId) {
      validIds.add(networkId);
    }
  }

  if (validIds.size > 0) {
    clearOtherSelections();
    selectedIds.value = validIds;
    selectionVersion.value = selectionVersion.value + 1;
    updateSelectionType();
  }
};

export const clearSelection = (): void => {
  clearOtherSelections();
  updateSelectionType();
};

export const selectNpc = ({ networkId }: { networkId: number }): void => {
  clearOtherSelections();
  selectedNpcId.value = networkId;
  updateSelectionType();
};

export const selectWorldObject = ({ object }: { object: WorldObjectSnapshot }): void => {
  clearOtherSelections();
  selectedWorldObjectId.value = object.networkId;

  if (TERRITORY_FLAG_TYPES.has(object.objectType)) {
    const territory = getTerritoryByFlagPosition({ tileX: object.tileX, tileY: object.tileY });
    if (territory) {
      selectTerritory({ networkId: territory.networkId });
    }
  }

  updateSelectionType();
};

export const deselectWorldObject = (): void => {
  selectedWorldObjectId.value = null;
  deselectTerritory();
  updateSelectionType();
};

export const deselectNpc = (): void => {
  selectedNpcId.value = null;
  updateSelectionType();
};

export const isSelected = ({ networkId }: { networkId: number }): boolean => {
  return selectedIds.value.has(networkId);
};

export const isNpcSelected = ({ networkId }: { networkId: number }): boolean => {
  return selectedNpcId.value === networkId;
};

export const getSelectedIds = (): number[] => {
  return Array.from(selectedIds.value);
};

// ============================================================================
// Input Handling
// ============================================================================

export const onMouseDown = ({
  worldX,
  worldY,
  screenX,
  screenY,
  suppressDrag = false,
}: {
  worldX: number;
  worldY: number;
  screenX: number;
  screenY: number;
  suppressDrag?: boolean;
}): void => {
  dragState.value = {
    active: true,
    isDragging: false,
    suppressDrag,
    startX: worldX,
    startY: worldY,
    screenStartX: screenX,
    screenStartY: screenY,
  };
  selectionBox.value = null;
};

export const onMouseMove = ({ worldX, worldY }: { worldX: number; worldY: number }): void => {
  const state = dragState.value;

  if (!state.active || state.suppressDrag) {
    return;
  }

  const dx = Math.abs(worldX - state.startX);
  const dy = Math.abs(worldY - state.startY);

  if (dx > DRAG_THRESHOLD || dy > DRAG_THRESHOLD) {
    dragState.value = { ...state, isDragging: true };
    selectionBox.value = {
      startX: state.startX,
      startY: state.startY,
      endX: worldX,
      endY: worldY,
    };
  }
};

// Handle box selection completion
const handleBoxSelection = ({
  box,
  state,
  screenX,
  screenY,
}: {
  box: SelectionBox;
  state: DragState;
  screenX: number;
  screenY: number;
}): void => {
  const screenBox: ScreenBox = {
    left: Math.min(state.screenStartX, screenX),
    top: Math.min(state.screenStartY, screenY),
    right: Math.max(state.screenStartX, screenX),
    bottom: Math.max(state.screenStartY, screenY),
  };
  selectPawnsInBox({ box, screenBox });
};

// Find pawn at tile position
const findPawnAtTile = ({ tileX, tileY }: { tileX: number; tileY: number }): PawnData | null => {
  for (const pawn of pawns.value.values()) {
    if (Math.floor(pawn.x) === tileX && Math.floor(pawn.y) === tileY) {
      return pawn;
    }
  }
  return null;
};

// Handle single click selection
const handleClickSelection = ({
  worldX,
  worldY,
  additive,
}: {
  worldX: number;
  worldY: number;
  additive: boolean;
}): void => {
  const tileX = Math.floor(worldX / TILE_SIZE);
  const tileY = Math.floor(worldY / TILE_SIZE);

  const foundPawn = findPawnAtTile({ tileX, tileY });
  if (foundPawn) {
    selectPawn({ networkId: foundPawn.networkId, additive });
    return;
  }

  const foundNpc = getNpcAtPosition({ worldX, worldY });
  if (foundNpc) {
    selectNpc({ networkId: foundNpc.networkId });
    return;
  }

  const foundObject = getWorldObjectAtPosition({ tileX, tileY });
  if (foundObject) {
    selectWorldObject({ object: foundObject });
    return;
  }

  if (!additive) {
    clearSelection();
  }
};

export const onMouseUp = ({
  worldX,
  worldY,
  screenX,
  screenY,
  additive = false,
}: {
  worldX: number;
  worldY: number;
  screenX: number;
  screenY: number;
  additive?: boolean;
}): void => {
  const state = dragState.value;
  const box = selectionBox.value;

  if (state.isDragging && box) {
    handleBoxSelection({ box, state, screenX, screenY });
  } else {
    handleClickSelection({ worldX, worldY, additive });
  }

  dragState.value = {
    active: false,
    isDragging: false,
    suppressDrag: false,
    startX: 0,
    startY: 0,
    screenStartX: 0,
    screenStartY: 0,
  };
  selectionBox.value = null;
};

// ============================================================================
// Cleanup Handlers
// ============================================================================

export const onPawnRemoved = ({ networkId }: { networkId: number }): void => {
  if (selectedIds.value.has(networkId)) {
    const next = new Set(selectedIds.value);
    next.delete(networkId);
    selectedIds.value = next;
    selectionVersion.value = selectionVersion.value + 1;
    updateSelectionType();
  }
};

export const onNpcRemoved = ({ networkId }: { networkId: number }): void => {
  if (selectedNpcId.value === networkId) {
    selectedNpcId.value = null;
    updateSelectionType();
  }
};
